import { v4 as uuidv4 } from 'uuid';
import isbot from 'isbot';
import { get, find, reduce, includes } from 'lodash';
import fetchPageInfo from './pageRouter';
import { getCookie, setCookie } from './cookie';
import { TRACKING_SESSION_KEY, USER_COUNTRY_CODE, ACTIVE_PROFILE_ID } from '../constants/cookieConstants';
import { ARTIST_TYPE_ID, AGENT_TYPE_ID, AOS_TYPE_ID } from '../constants/consts';
import { getLanguage } from './queriesUtil';
import { getRouteWithSlug } from './common';
import route from '../constants/routes';

const website_version = process.env.WEBSITE_VERSION || 'local';
// eslint-disable-next-line prefer-destructuring
const NEXT_PUBLIC_REQUEST_COUNTRY_HEADER = process.env.NEXT_PUBLIC_REQUEST_COUNTRY_HEADER;

export const isTrackingDisabled = req => {
  const isBot = isbot(req.get('user-agent'));
  const regions = process.env.EXCLUDE_REGIONS_FOR_BOTS;
  let disableTracking = false;
  if (regions) {
    const regionList = regions.split(',');
    const isRegionExist = regionList.find(country => req.headers[NEXT_PUBLIC_REQUEST_COUNTRY_HEADER] === country);
    disableTracking = isRegionExist ? isBot : false;
  } else {
    disableTracking = isBot;
  }
  return disableTracking;
};

export const getArtistProfileDetails = profile => {
  const socialProfiles = reduce(
    get(profile, 'contacts', []),
    (acc, contact) => {
      if (contact.contactType.slug === 'sns') {
        acc = {
          ...acc,
          [contact.name]: contact.value,
        };
      }

      return acc;
    },
    {},
  );

  return {
    'First & Middle name': profile?.firstName,
    'Last name': profile?.lastName,
    'Profile url': `${window.location.protocol}//${window.location.host}${getRouteWithSlug(route.ARTISTS, profile)}/`,
    Profession: profile?.profession?.qualification
      ? profile?.profession?.qualification?.name
      : profile?.profession?.name,
    'Profile photo': profile?.image?.original,
    Alias: profile?.alias,
    Mobile: find(get(profile, 'contacts', []), { name: 'phone' })?.value,
    'Contact e-mail': find(get(profile, 'contacts', []), { name: 'email' })?.value,
    'Website url': find(get(profile, 'contacts', []), { name: 'website' })?.value,
    'Page on Agency web-site': find(get(profile, 'contacts', []), { name: 'companywebsite' })?.value,
    Bio: includes(profile?.completedSections, 'Biography').toString(),
    Citizenship: profile?.personal?.citizenship,
    'Birth data': profile?.personal?.birth,
    'Social presence': socialProfiles,
    Video: includes(profile?.completedSections, 'Video').toString(),
    Images: includes(profile?.completedSections, 'Images')?.toString(),
    'Review & publications': includes(profile?.completedSections, 'Reviews & Publications').toString(),
  };
};

const getProfileData = ({ profile, activeSubscription }) => {
  const profileType = profile?.profileType?.id;
  let profileDetails = {
    'Active Profile ID': profile?.id,
    'Number of profiles': profile?.allProfileWithAccess,
    'Registration date': profile?.registrationCompleteDate,
    'Profile created date': profile?.createdAt,
    'Hidden profile': profile?.isHidden?.toString(),
    'Profile Type': profile?.profileType?.id,
    isPro: profile?.isPro?.toString(),
    'Subscription status': profile?.subscriptionStatus,
    'Subscription start date': activeSubscription?.[0]?.startAt,
    'Subscription renew date': activeSubscription?.[0]?.renewAt,
    'Subscription name': activeSubscription?.[0]?.subscription?.description,
    Addon: activeSubscription?.[0]?.addons,
  };

  switch (profileType) {
    case ARTIST_TYPE_ID:
      const artistInfo = getArtistProfileDetails(profile);

      profileDetails = {
        ...profileDetails,
        ...artistInfo,
      };
      break;
    case AGENT_TYPE_ID:
      profileDetails = {
        ...profileDetails,
        'Agency ID': profile?.agencies?.[0]?.id,
        'Profile url': `${window.location.protocol}//${window.location.host}${getRouteWithSlug(
          route.MANAGERS,
          get(profile, 'agencies[0]'),
        )}`,
      };
      break;
    case AOS_TYPE_ID:
      profileDetails = {
        ...profileDetails,
        'Company ID': profile?.companies?.[0]?.company?.id,
        'Profile url': `${window.location.protocol}//${window.location.host}${getRouteWithSlug(
          `${route.COMPANIES}/${get(profile, 'companies[0].company.country.slug')}`,
          get(profile, 'companies[0].company'),
        )}`,
      };
      break;
    default:
      break;
  }

  return profileDetails;
};

export const trackSearch = (item, value) => ({
  name: TRACK_EVENTS.SEARCH_TRACK,
  item: SEARCH_LABELS.DYNAMIC_FIELDS[item] || item,
  value,
});

export const trackSort = value => ({
  name: TRACK_EVENTS.SEARCH_SORT,
  sort_by: SORT_LABELS[value] || value,
});

export const trackClearForm = () => ({
  name: TRACK_EVENTS.SEARCH_CLEAR,
});

export const trackSearchFilter = (filter_on, filter_value, namedValue = '') => ({
  name: TRACK_EVENTS.SEARCH_LIST_FILTER,
  filter_on: SEARCH_FILTER_LABELS.DYNAMIC_FIELDS[filter_on] || filter_on,
  filter_value,
  filter_named_value: namedValue,
});

export const trackSearchGo = query => ({
  name: TRACK_EVENTS.SEARCH_GO,
  ...formatQuery(query),
});

const isInternalUser = email => {
  if (!email) return 'false';

  return [
    '@transfunnel',
    '@operabase',
    '@truelinked',
    '@artsconsolidated',
    '@softteco',
    'ravikiran',
    'glgorb',
    'hleb',
    'rajesh',
    'mukund',
    'rekha',
    'test',
    'tes-tes',
    'atul.',
    'alemuresti@gmail.com',
  ]
    .some(str => email.includes(str))
    .toString();
};

export const trackUserAction = ({ payload, router, profile, userDetails, activeSubscription }) => {
  const url = router.asPath;
  const path = router.pathname === '/' ? '/' : url.slice(0, url.lastIndexOf('/'));
  const pageInfo = fetchPageInfo(path, router.query);
  const profileDetails = getProfileData({ profile, activeSubscription });

  const data = { ...profileDetails };
  data.page_type = pageInfo && pageInfo.page_type ? pageInfo.page_type : url;
  data.session_id = generateSessionId();
  data.website_version = website_version;
  data.req_country_code = getCountryCode();
  data.req_profile_id = getProfileId();
  data.language = getLanguage();
  data.page_url = url;
  data.user_created_date = userDetails?.createdAt;
  data.is_internal_user = isInternalUser(userDetails?.email);

  const trackData = { ...data, ...payload };

  return trackData;
};

const inThirtyMinutes = () => new Date(new Date().getTime() + 30 * 60 * 1000);

export const generateSessionId = () => {
  let sessionId = getCookie(TRACKING_SESSION_KEY);
  if (!sessionId) {
    sessionId = uuidv4();
    setCookie(TRACKING_SESSION_KEY, sessionId, inThirtyMinutes());
  }
  return sessionId;
};

export const updateSessionIdInCookie = () => {
  const sessionId = getCookie(TRACKING_SESSION_KEY) || uuidv4();
  setCookie(TRACKING_SESSION_KEY, sessionId, inThirtyMinutes());
};

export const getCountryCode = () => getCookie(USER_COUNTRY_CODE);

export const userIdentifyEvent = (userData, eventType) => {
  const { name, email, id, profileId, profileInfo } = userData;
  const profileDetails = getProfileData({ profile: profileInfo });
  const traits = {
    ...profileDetails,
    name,
    email,
    event: eventType || 'logged_in',
    req_profile_id: profileId,
    req_country_code: getCountryCode(),
    website_version,
    session_id: generateSessionId(),
    language: getLanguage(),
    user_created_date: userData?.createdAt,
    is_internal_user: isInternalUser(userData?.email),
  };

  if (typeof window !== 'undefined') {
    window.dataLayer?.push({ ...traits });
  }
  if (window && window.Appcues) {
    window.Appcues.identify(id, traits);
  }
};

export const resetUserOnLogout = userData => {
  if (typeof window !== 'undefined') {
    userIdentifyEvent(userData, 'logged_out');
    if (window && window.Appcues) {
      window.Appcues.reset();
    }
  }
};

export const pageTracking = ({ router, url, profile, userDetails }) => {
  if (typeof window !== 'undefined') {
    const path = url;
    let pageInfo = fetchPageInfo(path, router.query);
    const profileDetails = getProfileData({ profile });

    pageInfo = {
      ...pageInfo,
      ...profileDetails,
    };

    pageInfo.session_id = generateSessionId();
    pageInfo.req_country_code = getCountryCode();
    pageInfo.req_profile_id = getProfileId();
    pageInfo.language = getLanguage();
    pageInfo.page_url = router.asPath;
    pageInfo.user_created_date = userDetails?.createdAt;
    pageInfo.is_internal_user = isInternalUser(userDetails?.email);

    return pageInfo;
  }

  return {};
};

const getProfileId = () => getCookie(ACTIVE_PROFILE_ID) || null;

export const clickReadMore = (data = {}) => ({
  name: TRACK_EVENTS.CLICK_READ_MORE,
  ...data,
});

export const clickFbTab = (data = {}) => ({
  name: TRACK_EVENTS.CLICK_FB_TAB,
  ...data,
});

export const clickDetailsLink = (data = {}) => ({
  name: TRACK_EVENTS.CLICK_SEE_MORE,
  ...data,
});

export const trackExpandProduction = (data = {}) => ({
  name: TRACK_EVENTS.EXPAND_PRODUCTION,
  ...data,
});

export const trackViewAll = (tab, data = {}) => ({
  name: TRACK_EVENTS.VIEW_ALL_TAB(tab),
  ...data,
});

export const clickProPageTab = (tab, data = {}) => ({
  name: TRACK_EVENTS.CLICK_PRO_PAGE_TAB,
  tab_item: tab,
  ...data,
});

export const trackMediaPlayer = (data = {}) => ({
  name: TRACK_EVENTS.CLICK_MEDIA_PLAYER,
  ...data,
});

export const loadMoreArtistPerformances = (data = {}) => ({
  name: TRACK_EVENTS.LOAD_MORE_ARTIST_PERFORMANCES,
  ...data,
});

export const trackViewArtistProfile = (data = {}) => ({
  name: TRACK_EVENTS.VIEW_ARTIST_PROFILE,
  ...data,
});

export const trackViewArtistEditProfile = (data = {}) => ({
  name: TRACK_EVENTS.VIEW_ARTIST_EDIT_PROFILE,
  ...data,
});

export const trackUpdateArtistProfile = (data = {}, propertyName) => ({
  name: `${TRACK_EVENTS.UPDATE_ARTIST_PROFILE}_${propertyName}`,
  ...data,
});

export const trackViewAOSProfile = (data = {}) => ({
  name: TRACK_EVENTS.VIEW_AOS_PROFILE,
  ...data,
});

export const trackViewAOSEditProfile = (data = {}) => ({
  name: TRACK_EVENTS.VIEW_AOS_EDIT_PROFILE,
  ...data,
});

export const trackUpdateAOSProfile = (data = {}, propertyName) => ({
  name: `${TRACK_EVENTS.UPDATE_AOS_PROFILE}_${propertyName}`,
  ...data,
});

export const trackShareAccess = (data = {}) => ({
  name: TRACK_EVENTS.SHARE_ACCESS,
  ...data,
});

export const trackViewAgencyProfile = (data = {}) => ({
  name: TRACK_EVENTS.VIEW_AGENCY_PROFILE,
  ...data,
});

export const trackViewAgencyEditProfile = (data = {}) => ({
  name: TRACK_EVENTS.VIEW_AGENCY_EDIT_PROFILE,
  ...data,
});

export const trackRequestArtistAccess = (data = {}) => ({
  name: TRACK_EVENTS.REQUEST_ARTIST_ACCESS,
  ...data,
});

export const trackAddAgencyArtist = (data = {}) => ({
  name: TRACK_EVENTS.ADD_AGENCY_ARTIST,
  ...data,
});

export const trackUpdateAgencyProfile = (data = {}) => ({
  name: TRACK_EVENTS.UPDATE_AGENCY_PROFILE,
  ...data,
});

export const trackUpdateAgencyAgent = (data = {}) => ({
  name: TRACK_EVENTS.UPDATE_AGENCY_AGENT,
  ...data,
});

export const trackLoggedIn = (data = {}) => ({
  name: TRACK_EVENTS.LOGGED_IN,
  ...data,
});

export const trackFormSubmit = (data = {}) => ({
  name: TRACK_EVENTS.FORM_SUBMIT,
  ...data,
});

export const pingSegment = () => ({
  name: TRACK_EVENTS.PING,
});

export const trackEvent = ({ name, data }) => ({
  name: TRACK_EVENTS[name] || name,
  ...data,
});

export const trackPaymentCompleted = (data = {}) => ({
  name: TRACK_EVENTS.PAYMENT_COMPLETED,
  ...data,
});

export const trackClickTickets = (data = {}) => ({
  name: TRACK_EVENTS.CLICKED_TICKETS,
  ...data,
});

export const trackClickedFAQClickHere = (profileType, data = {}) => ({
  name: TRACK_EVENTS.CLICKED_FAQ_CLICK_HERE(profileType),
  ...data,
});

export const trackVideoClick = (type, data = {}) => ({
  name: TRACK_EVENTS[`CLICK_VIDEO_${type.toUpperCase()}`] || TRACK_EVENTS.CLICK_VIDEO_BUTTONS(type),
  ...data,
});

export const trackOnVideoHover = (data = {}) => ({
  name: TRACK_EVENTS.HOVER_ON_VIDEO_POSTER,
  ...data,
});

export const trackClickReview = (data = {}) => ({
  name: TRACK_EVENTS.CLICK_VIDEO_REVIEWS_LINK,
  ...data,
});

export const getVideoPrefix = isWelcomeVideo =>
  isWelcomeVideo ? TRACK_EVENTS.WELCOME_VIDEO : TRACK_EVENTS.SECTION_VIDEO;
export const trackVideoClose = (prefix, data = {}) => ({
  name: prefix,
  action_type: VIDEO_EVENTS.CLICK_CLOSE,
  ...data,
});
export const trackVideoMarkDoNotShowAgain = (prefix, data = {}) => ({
  name: prefix,
  action_type: VIDEO_EVENTS.MARK_DO_NOT_SHOW_AGAIN,
  ...data,
});
export const trackVideoOpen = (prefix, data = {}) => ({
  name: prefix,
  action_type: VIDEO_EVENTS.CLICK_OPEN,
  ...data,
});
export const trackVideoSkip = (prefix, data = {}) => ({ name: prefix, action_type: VIDEO_EVENTS.CLICK_SKIP, ...data });

const VIDEO_EVENTS = {
  CLICK_CLOSE: 'click_close_button',
  MARK_DO_NOT_SHOW_AGAIN: 'mark_do_not_show_again',
  CLICK_OPEN: 'click_open_button',
  CLICK_SKIP: 'click_skip_button',
};

export const TRACK_EVENTS = {
  SEARCH_TRACK: 'search_track',
  SEARCH_GO: 'search_go',
  SEARCH_CLEAR: 'search_clear',
  SEARCH_SORT: 'search_sort',
  SEARCH_LIST_FILTER: 'search_list_filter',
  CLICK_FB_TAB: 'click_tab',
  CLICK_PRO_PAGE_TAB: `pro_page_tab`,
  CLICK_READ_MORE: 'click_read_more',
  CLICK_SEE_MORE: 'click_see_more',
  EXPAND_PRODUCTION: 'expand_production',
  VIEW_ALL_TAB: tab => `artist_${tab}`,
  CLICK_MEDIA_PLAYER: 'click_media_player',
  LOAD_MORE_ARTIST_PERFORMANCES: 'load_more_artist_performances',
  VIEW_ARTIST_PROFILE: 'view_artist_profile',
  VIEW_ARTIST_EDIT_PROFILE: 'view_artist_edit_profile',
  UPDATE_ARTIST_PROFILE: 'update_artist_profile',
  VIEW_AOS_PROFILE: 'view_aos_profile',
  VIEW_AOS_EDIT_PROFILE: 'view_aos_edit_profile',
  UPDATE_AOS_PROFILE: 'update_aos_profile',
  SHARE_ACCESS: 'share_access',
  VIEW_AGENCY_PROFILE: 'view_agency_profile',
  VIEW_AGENCY_EDIT_PROFILE: 'view_agency_edit_profile',
  REQUEST_ARTIST_ACCESS: 'request_artist_access',
  ADD_AGENCY_ARTIST: 'add_agency_artist',
  UPDATE_AGENCY_PROFILE: 'update_agency_profile',
  UPDATE_AGENCY_AGENT: 'update_agency_agent',
  LOGGED_IN: 'logged_in',
  PING: 'ping',
  FORM_SUBMIT: 'form_submit',
  PAYMENT_COMPLETED: 'payment_completed',
  CLICKED_TICKETS: 'clicked_tickets',
  CLICKED_FAQ_CLICK_HERE: profileType => `clicked_FAQ_click_here_${profileType}`,
  BILLING_ADDRESS_POPUP_SHOWN: 'shown_billing_popup',
  BILLING_ADDRESS_POPUP_COUNTRY_UPDATED: 'updated_billing_country',
  BILLING_ADDRESS_ACS_UPDATED: 'updated_billing_info',
  CLICK_REGISTER_NOW_TOP: 'click_register_now_top',
  CLICK_REGISTER_NOW_BELOW: 'click_register_now_below',
  CLICK_LERN_MORE: 'click_learn_more',
  START_REGISTRATION: 'start_registration',
  CREATE_PROFILE: 'create_profile',
  CLICK_CONTACT_US_REGISTRATION: 'click_contact_us_registration',
  CLICK_VIDEO_BUTTONS: buttonType => `click_video_${buttonType}`,
  CLICK_VIDEO_POSTER: 'click_video_poster',
  CLICK_VIDEO_WATCH: 'click_video_watch',
  CLICK_VIDEO_WATCH_NOW: 'click_video_watch_now',
  CLICK_VIDEO_LIVESTREAM: 'click_video_livestream',
  CLICK_VIDEO_TRAILER: 'click_video_trailer',
  CLICK_VIDEO_ADD: 'click_video_add',
  CLICK_VIDEO_LIVE_NOW: 'click_video_live_now',
  CLICK_VIDEO_LINK: 'click_video_link',
  CLICK_VIDEO_IMAGES_LINK: 'click_video_images_link',
  CLICK_VIDEO_REVIEWS_LINK: 'click_review_link',
  HOVER_ON_VIDEO_POSTER: 'hover_on_video_poster',
  WELCOME_VIDEO: 'welcome_video',
  SECTION_VIDEO: 'section_video',
};

export const formatQuery = query => {
  const obj = {};
  for (const [key, value] of Object.entries(query)) {
    const newKey = SEARCH_LABELS.DYNAMIC_FIELDS[key] || key;
    const isKeyExist = searchExhaustiveList.find(value => value === newKey);
    if (isKeyExist) {
      obj[newKey] = value;
    } else {
      obj.other = {
        key: newKey,
        value,
      };
    }
  }
  return obj;
};

export const searchExhaustiveList = [
  'country',
  'city',
  'company',
  'composer',
  'work',
  'from',
  'to',
  'voice',
  'profession',
  'date',
  'status',
  'age_limit_men',
  'age_limit_women',
  'year',
  'seasons',
  'production',
];

export const SEARCH_LABELS = {
  DYNAMIC_FIELDS: {
    country_id: 'country',
    date_from: 'from',
    date_to: 'to',
    city_id: 'city',
    composer_id: 'composer',
    company_id: 'company',
    work_id: 'work',
    managers_id: 'manager',
    competition_id: 'competition',
    city: 'city',
    status: 'status',
    age_limit_men: 'age_limit_men',
    age_limit_women: 'age_limit_women',
    year: 'year',
  },
  COMPANY: 'company',
  MANAGER: 'manager',
  COMPETITIONS: 'competitions',
  DATE: 'date',
  ARTIST: 'artist',
  FESTIVALS: 'festivals',
  VENUES: 'venues',
  PAGE: 'page',
  SUBSCRIPTION: 'subscription',
  PAYMENT: 'payment',
};

export const SORT_LABELS = {
  year: 'performance_date',
  'productions.name': 'musical_work_name',
  'workComposer.name': 'composer_name',
  'country.name': 'country_name',
  'city.name': 'city_name',
  'conductor.name': 'conductor_name',
  'director.name': 'director_name',
  'company.name': 'company_name',
};

export const SEARCH_FILTER_LABELS = {
  COUNTRY: 'country',
  SEASONS: 'seasons',
  DYNAMIC_FIELDS: {
    profession_id: 'profession',
    profession_qualification_id: 'voice',
    country_id: 'country',
    years: 'years',
    work_id: 'work',
    year: 'year',
    company_id: 'company',
    page_id: 'page',
    subscription_id: 'subscription',
    payment_id: 'payment',
  },
};
